import { useCreation } from '@euler/utils/hooks/useCreation';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  CaptureErrorCode,
  CaptureMessage,
  SpeechStart,
} from '../capture/capturePage';
import { SpeechCaptureHandle } from '../capture/speechCaptureTypes';

/** How a hold sounded: what a host keeps of it, if anything. */
export interface SpokenVoice {
  /** Input levels (0 to 1), about ten a second, early to late. */
  levels: readonly number[];
  durationMs: number;
  /** The hold as a 16 kHz mono WAV, base64, from an endpoint that sends it. */
  audio: string | null;
  /** The server's token for the voice it parked, from one that parks it. */
  token: string | null;
}

/**
 * How a capture was started: held (the mic, or the bar on a phone), or
 * dictated into the box from its mic.
 */
export type VoiceTurnVia = 'hold' | 'dictate';

/**
 * What the words are for once let go: sent as the line, opened to edit
 * first, or put in the box to send later.
 */
export type VoiceTurnOutcome = 'send' | 'edit' | 'insert';

export type VoiceTurnState =
  | { kind: 'idle' }
  /** Talking: the words as they are recognised. */
  | { kind: 'holding'; caption: string; via: VoiceTurnVia }
  /** Let go: waiting for the final sentence. */
  | { kind: 'recognizing'; caption: string; outcome: VoiceTurnOutcome };

/** Words recognised for the user to look at before they go. */
export interface VoiceTurnText {
  outcome: Exclude<VoiceTurnOutcome, 'send'>;
  text: string;
  voice: SpokenVoice;
}

export type VoiceNotice =
  | 'unheard'
  | 'microphone'
  | 'credits'
  | 'busy'
  | 'too_long'
  | 'not_enabled'
  | 'failed'
  | null;

const kNoticeOf: Partial<Record<CaptureErrorCode, VoiceNotice>> = {
  [CaptureErrorCode.Microphone]: 'microphone',
  [CaptureErrorCode.Credits]: 'credits',
  [CaptureErrorCode.Busy]: 'busy',
  [CaptureErrorCode.TooLong]: 'too_long',
  [CaptureErrorCode.FeatureNotEnabled]: 'not_enabled',
  [CaptureErrorCode.TrialExhausted]: 'credits',
};

/** The final sentence arrives soon after release, or it is not coming. */
const kFinalTimeoutMs = 8_000;

/**
 * Hold to talk: while held the capture streams and captions grow; on
 * release the final sentence is sent (`onLine`), or, as the release asked,
 * words to edit or to put in the box (`texts$`). `onPress` runs as a hold
 * begins (coaching stops the customer's voice, so the trainee can talk
 * over them).
 */
export function useVoiceTurn(
  capture: RefObject<SpeechCaptureHandle>,
  /**
   * The endpoint's start payload for a hold, by how it began (a tap to
   * dictate may ask the server to end it on the speaker's silence).
   */
  startOf: (via: VoiceTurnVia) => SpeechStart,
  onLine: (line: string, voice: SpokenVoice) => void,
  onPress: () => void,
) {
  const state$ = useCreation(
    () => new BehaviorSubject<VoiceTurnState>({ kind: 'idle' }),
    [],
  );
  const level$ = useCreation(() => new BehaviorSubject(0), []);
  const notice$ = useCreation(() => new BehaviorSubject<VoiceNotice>(null), []);
  const texts$ = useCreation(() => new Subject<VoiceTurnText>(), []);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clearTimer, []);
  const line = useRef(onLine);
  line.current = onLine;
  // The voice of the hold under way: its levels, and how long it was held.
  const heard = useRef({
    levels: [] as number[],
    startedAt: 0,
    durationMs: 0,
    audio: null as string | null,
  });

  const press = useCallback(
    (via: VoiceTurnVia = 'hold') => {
      if (state$.getValue().kind !== 'idle') return;
      onPress();
      notice$.next(null);
      heard.current = {
        levels: [],
        startedAt: Date.now(),
        durationMs: 0,
        audio: null,
      };
      state$.next({ kind: 'holding', caption: '', via });
      capture.current?.start(startOf(via));
    },
    [startOf, capture, notice$, onPress, state$],
  );

  const release = useCallback(
    (outcome: VoiceTurnOutcome = 'send') => {
      const state = state$.getValue();
      if (state.kind !== 'holding') return;
      state$.next({ kind: 'recognizing', caption: state.caption, outcome });
      heard.current.durationMs = Date.now() - heard.current.startedAt;
      level$.next(0);
      capture.current?.stop();
      clearTimer();
      timer.current = setTimeout(() => {
        if (state$.getValue().kind !== 'recognizing') return;
        capture.current?.cancel();
        state$.next({ kind: 'idle' });
        notice$.next('failed');
      }, kFinalTimeoutMs);
    },
    [capture, level$, notice$, state$],
  );

  const cancel = useCallback(() => {
    if (state$.getValue().kind === 'idle') return;
    clearTimer();
    capture.current?.cancel();
    level$.next(0);
    state$.next({ kind: 'idle' });
  }, [capture, level$, state$]);

  const receive = useCallback(
    (message: CaptureMessage) => {
      const state = state$.getValue();
      switch (message.kind) {
        case 'partial':
          if (state.kind !== 'idle')
            state$.next({ ...state, caption: message.text });
          break;
        case 'level':
          if (state.kind === 'holding') {
            level$.next(message.value);
            heard.current.levels.push(message.value);
          }
          break;
        case 'clip':
          if (state.kind === 'recognizing') heard.current.audio = message.audio;
          break;
        case 'final': {
          if (state.kind === 'idle') break;
          // Still held: the server ended it on the speaker's silence, as a
          // tap to dictate asks; it goes where letting go would take it.
          const outcome: VoiceTurnOutcome =
            state.kind === 'recognizing'
              ? state.outcome
              : state.via === 'dictate'
              ? 'insert'
              : 'send';
          if (state.kind === 'holding') {
            heard.current.durationMs = Date.now() - heard.current.startedAt;
            level$.next(0);
          }
          clearTimer();
          state$.next({ kind: 'idle' });
          const text = message.text.trim();
          if (!text) {
            notice$.next('unheard');
            break;
          }
          const voice: SpokenVoice = {
            levels: heard.current.levels,
            durationMs: heard.current.durationMs,
            audio: heard.current.audio,
            token: message.voiceToken,
          };
          if (outcome === 'send') line.current(text, voice);
          else texts$.next({ outcome, text, voice });
          break;
        }
        case 'error':
          clearTimer();
          level$.next(0);
          state$.next({ kind: 'idle' });
          notice$.next(kNoticeOf[message.code] ?? 'failed');
          break;
        default:
          break;
      }
    },
    [level$, notice$, state$, texts$],
  );

  return useMemo(
    () => ({
      state$,
      level$,
      notice$,
      texts$,
      press,
      release,
      cancel,
      receive,
    }),
    [state$, level$, notice$, texts$, press, release, cancel, receive],
  );
}

export type VoiceTurn = ReturnType<typeof useVoiceTurn>;
